import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock,
  Plus,
  Pencil,
  Trash2,
  LogOut,
  ArrowLeft,
  Home,
  ImagePlus,
  Star,
  X,
  CheckSquare,
} from "lucide-react";
import {
  adminLogin,
  adminVerify,
  fetchProperties,
  fetchProperty,
  createProperty,
  updateProperty,
  deleteProperty,
  uploadPhoto,
  formatPrice,
} from "../lib/api";
import { PROPERTY_TYPES, typeLabel } from "../data/content";

const EMPTY_FORM = {
  title: "",
  description: "",
  price: "",
  location: "",
  operation: "venta",
  property_type: "casa",
  bedrooms: "",
  bathrooms: "",
  area_m2: "",
  photos: [],
  features: [],
  featured: false,
  status: "disponible",
};

const MAX_PHOTOS = 20;

// ──────────────────────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [authed,          setAuthed]          = useState(false);
  const [authLoading,     setAuthLoading]     = useState(true);
  const [password,        setPassword]        = useState("");
  const [loginError,      setLoginError]      = useState("");
  const [loginLoading,    setLoginLoading]    = useState(false);

  const [properties,      setProperties]      = useState([]);
  const [loadingList,     setLoadingList]     = useState(false);
  const [view,            setView]            = useState("list");  // list | form
  const [editingId,       setEditingId]       = useState(null);
  const [form,            setForm]            = useState(EMPTY_FORM);
  const [saving,          setSaving]          = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [featureText,     setFeatureText]     = useState("");
  const fileInputRef = useRef(null);

  // ── Auth check on mount ────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("rr_admin_token");
    if (!token) { setAuthLoading(false); return; }
    adminVerify()
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem("rr_admin_token"); })
      .finally(() => setAuthLoading(false));
  }, []);

  // ── Load properties ────────────────────────────────────────────────────────
  const loadProperties = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await fetchProperties();
      setProperties(data);
    } catch {
      toast.error("No se pudieron cargar las propiedades");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { if (authed) loadProperties(); }, [authed, loadProperties]);

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setLoginError("Ingrese la contraseña"); return; }
    setLoginLoading(true);
    setLoginError("");
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem("rr_admin_token", token);
      setAuthed(true);
    } catch {
      setLoginError("Contraseña incorrecta");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("rr_admin_token");
    setAuthed(false);
    setPassword("");
    setView("list");
  };

  // ── Create / Edit ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFeatureText("");
    setView("form");
  };

  const openEdit = async (id) => {
    try {
      const full = await fetchProperty(id);
      setForm({
        title:         full.title         || "",
        description:   full.description   || "",
        price:         full.price         || "",
        location:      full.location      || "",
        operation:     full.operation     || "venta",
        property_type: full.property_type || "casa",
        bedrooms:      full.bedrooms      || "",
        bathrooms:     full.bathrooms     || "",
        area_m2:       full.area_m2       || "",
        photos:        Array.isArray(full.photos)   ? full.photos   : [],
        features:      Array.isArray(full.features) ? full.features : [],
        featured:      !!full.featured,
        status:        full.status || "disponible",
      });
      setEditingId(id);
      setFeatureText("");
      setView("form");
    } catch (err) {
      toast.error("No se pudo cargar la propiedad para editar");
    }
  };

  // ── Photo upload (multipart) ───────────────────────────────────────────────
  const handlePhotoFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const available = MAX_PHOTOS - form.photos.length;
    if (available <= 0) { toast.error(`Máximo ${MAX_PHOTOS} fotos`); return; }
    const toUpload = files.slice(0, available);

    setUploadingPhotos(true);
    try {
      const results = await Promise.all(
        toUpload.filter((f) => f.type.startsWith("image/")).map((f) => uploadPhoto(f))
      );
      if (results.length) {
        setForm((prev) => ({ ...prev, photos: [...prev.photos, ...results] }));
        toast.success(`${results.length} foto(s) subida(s)`);
      }
    } catch {
      toast.error("Error al subir las fotos. Intente de nuevo.");
    } finally {
      setUploadingPhotos(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) =>
    setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));

  // ── Features ───────────────────────────────────────────────────────────────
  const addFeature = () => {
    const text = featureText.trim();
    if (!text) return;
    if (form.features.length >= 20) { toast.error("Máximo 20 características"); return; }
    setForm((prev) => ({ ...prev, features: [...prev.features, text] }));
    setFeatureText("");
  };

  const removeFeature = (idx) =>
    setForm((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }));

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = {
      ...form,
      price:     Number(form.price)             || 0,
      bedrooms:  parseInt(form.bedrooms,  10)   || 0,
      bathrooms: Number(form.bathrooms)         || 0,
      area_m2:   Number(form.area_m2)           || 0,
      currency:  "MXN",
    };
    try {
      if (editingId) {
        await updateProperty(editingId, payload);
        toast.success("Propiedad actualizada");
      } else {
        await createProperty(payload);
        toast.success("Propiedad publicada");
      }
      setView("list");
      loadProperties();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesión expirada. Inicie sesión de nuevo.");
        handleLogout();
      } else {
        const msg = err?.response?.data?.detail || "Error al guardar";
        toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await deleteProperty(id);
      toast.success("Propiedad eliminada");
      setConfirmDeleteId(null);
      loadProperties();
    } catch {
      toast.error("Error al eliminar la propiedad");
    }
  };

  // ── Field helpers ──────────────────────────────────────────────────────────
  const setField = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Render: loading
  if (authLoading) {
    return (
      <div className="admin-loading" data-testid="admin-loading">
        <div className="spinner" />
      </div>
    );
  }

  // Render: login
  if (!authed) {
    return (
      <div className="admin-login-wrap" data-testid="admin-login-page">
        <form className="admin-login-card" onSubmit={handleLogin} data-testid="admin-login-form">
          <div className="admin-login-icon"><Lock size={28} strokeWidth={1.6} /></div>
          <h2 className="font-display admin-login-title">Administración</h2>
          <p className="admin-login-sub">Panel de propiedades de Inmobiliaria Rendón Ramos</p>
          <input
            className="admin-input"
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setLoginError(""); }}
            autoComplete="current-password"
            data-testid="admin-password-input"
          />
          {loginError && (
            <p className="admin-login-error" data-testid="admin-login-error">{loginError}</p>
          )}
          <button className="btn btn-royal w-full" type="submit" disabled={loginLoading} data-testid="admin-login-btn">
            {loginLoading ? "Verificando…" : "Entrar"}
          </button>
          <Link to="/" className="admin-back-link" data-testid="admin-back-link">
            <ArrowLeft size={15} /> Volver al sitio
          </Link>
        </form>
      </div>
    );
  }

  // Render: form
  if (view === "form") {
    return (
      <div className="admin-wrap" data-testid="admin-form-page">
        <div className="admin-header">
          <button className="btn btn-ghost" onClick={() => setView("list")} data-testid="admin-back-list-btn">
            <ArrowLeft size={17} /> Volver
          </button>
          <span className="admin-header-title font-display">
            {editingId ? "Editar propiedad" : "Nueva propiedad"}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut size={15} />
          </button>
        </div>

        <form className="admin-form" onSubmit={handleSave} data-testid="property-form">
          <div className="admin-form-grid">
            {/* ── Título ── */}
            <div className="admin-field admin-field-full">
              <label>Título *</label>
              <input className="admin-input" type="text" placeholder="Ej: Casa en venta — Col. Las Tunas" value={form.title} onChange={setField("title")} required data-testid="prop-title-input" />
            </div>

            {/* ── Precio ── */}
            <div className="admin-field">
              <label>Precio (MXN)</label>
              <input className="admin-input" type="number" placeholder="1500000" min="0" value={form.price} onChange={setField("price")} data-testid="prop-price-input" />
            </div>

            {/* ── Operación ── */}
            <div className="admin-field">
              <label>Operación</label>
              <select className="admin-input" value={form.operation} onChange={setField("operation")} data-testid="prop-operation-select">
                <option value="venta">Venta</option>
                <option value="renta">Renta</option>
              </select>
            </div>

            {/* ── Tipo ── */}
            <div className="admin-field">
              <label>Tipo de propiedad</label>
              <select className="admin-input" value={form.property_type} onChange={setField("property_type")} data-testid="prop-type-select">
                {PROPERTY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* ── Estado ── */}
            <div className="admin-field">
              <label>Estado</label>
              <select className="admin-input" value={form.status} onChange={setField("status")} data-testid="prop-status-select">
                <option value="disponible">Disponible</option>
                <option value="vendido">Vendido</option>
                <option value="rentado">Rentado</option>
              </select>
            </div>

            {/* ── Recámaras / Baños / m² ── */}
            <div className="admin-field">
              <label>Recámaras</label>
              <input className="admin-input" type="number" placeholder="0" min="0" value={form.bedrooms} onChange={setField("bedrooms")} data-testid="prop-bedrooms-input" />
            </div>
            <div className="admin-field">
              <label>Baños</label>
              <input className="admin-input" type="number" placeholder="0" min="0" step="0.5" value={form.bathrooms} onChange={setField("bathrooms")} data-testid="prop-bathrooms-input" />
            </div>
            <div className="admin-field">
              <label>Superficie (m²)</label>
              <input className="admin-input" type="number" placeholder="0" min="0" value={form.area_m2} onChange={setField("area_m2")} data-testid="prop-area-input" />
            </div>

            {/* ── Ubicación ── */}
            <div className="admin-field admin-field-full">
              <label>Ubicación</label>
              <input className="admin-input" type="text" placeholder="Ej: Calle Yasmín S/N, Col. Las Tunas, Técpan" value={form.location} onChange={setField("location")} data-testid="prop-location-input" />
            </div>

            {/* ── Descripción ── */}
            <div className="admin-field admin-field-full">
              <label>Descripción</label>
              <textarea className="admin-input" rows={4} placeholder="Descripción detallada de la propiedad…" value={form.description} onChange={setField("description")} data-testid="prop-description-input" />
            </div>

            {/* ── Fotos (file upload multipart) ── */}
            <div className="admin-field admin-field-full">
              <label>Fotos (máx. {MAX_PHOTOS} — se comprimen automáticamente)</label>
              <label className={`btn btn-ghost upload-btn ${uploadingPhotos ? "disabled" : ""}`} style={{ alignSelf: "flex-start", cursor: uploadingPhotos ? "not-allowed" : "pointer" }}>
                <ImagePlus size={17} />
                {uploadingPhotos ? "Subiendo…" : "Seleccionar imágenes"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handlePhotoFiles}
                  disabled={uploadingPhotos}
                  data-testid="prop-photos-input"
                />
              </label>
              <p style={{ fontSize: 12, color: "var(--rr-ink-soft)", marginTop: 4 }}>
                Selecciona una o varias imágenes desde tu dispositivo. Se comprimen automáticamente.
              </p>
              {form.photos.length > 0 && (
                <div className="photo-previews" data-testid="photo-previews">
                  {form.photos.map((p, i) => (
                    <div key={i} className="photo-preview" data-testid={`photo-preview-${i}`}>
                      <img src={p} alt={`Foto ${i + 1}`} onError={(e) => { e.target.style.opacity = "0.3"; }} />
                      <button type="button" className="photo-remove" onClick={() => removePhoto(i)} data-testid={`photo-remove-${i}`} title="Eliminar foto">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Características ── */}
            <div className="admin-field admin-field-full">
              <label>Características (máx. 20)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="admin-input"
                  style={{ flex: 1 }}
                  type="text"
                  placeholder="Ej: 3 recámaras, cocina integral, 2 garajes…"
                  value={featureText}
                  onChange={(e) => setFeatureText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  data-testid="prop-feature-input"
                />
                <button type="button" className="btn btn-ghost" onClick={addFeature} data-testid="prop-add-feature-btn" style={{ flexShrink: 0 }}>
                  <CheckSquare size={15} /> Agregar
                </button>
              </div>
              <p style={{ fontSize: 12, color: "var(--rr-ink-soft)", marginTop: 4 }}>
                Escribe cada característica y presiona Agregar (o Enter).
              </p>
              {form.features.length > 0 && (
                <div className="features-list" data-testid="features-list">
                  {form.features.map((f, i) => (
                    <span key={i} className="feature-tag" data-testid={`feature-tag-${i}`}>
                      {f}
                      <button type="button" onClick={() => removeFeature(i)} aria-label={`Eliminar ${f}`} data-testid={`feature-remove-${i}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ── Destacar ── */}
            <div className="admin-field admin-field-full">
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                <input type="checkbox" checked={form.featured} onChange={setField("featured")} data-testid="prop-featured-checkbox" style={{ width: 17, height: 17, accentColor: "var(--rr-gold-dark)" }} />
                <Star size={15} style={{ color: "var(--rr-gold-dark)" }} /> Destacar esta propiedad
              </label>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <button type="submit" className="btn btn-royal" disabled={saving} data-testid="prop-save-btn">
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Publicar propiedad"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setView("list")} data-testid="prop-cancel-btn">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Render: list
  return (
    <div className="admin-wrap" data-testid="admin-list-page">
      <div className="admin-header">
        <Link to="/" className="btn btn-ghost" data-testid="admin-home-link">
          <Home size={17} /> Sitio
        </Link>
        <span className="admin-header-title font-display">Panel de propiedades</span>
        <button className="btn btn-ghost btn-sm" onClick={handleLogout} data-testid="admin-logout-btn">
          <LogOut size={15} /> Salir
        </button>
      </div>

      <div className="admin-actions">
        <button className="btn btn-royal" onClick={openCreate} data-testid="admin-new-property-btn">
          <Plus size={17} /> Nueva propiedad
        </button>
        <span className="admin-count" data-testid="admin-property-count">
          {properties.length} propiedad{properties.length !== 1 ? "es" : ""}
        </span>
      </div>

      {loadingList ? (
        <div className="admin-loading-inline" data-testid="admin-loading-list">
          <div className="spinner" /> Cargando…
        </div>
      ) : properties.length === 0 ? (
        <div className="empty-state" data-testid="admin-empty">
          <p>No hay propiedades. Crea la primera.</p>
        </div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-properties-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Título</th>
                <th>Operación</th>
                <th>Precio</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => (
                <tr key={p.id} data-testid={`property-row-${p.id}`}>
                  <td>
                    {p.cover_photo ? (
                      <img src={p.cover_photo} alt={p.title} className="admin-thumb" loading="lazy" />
                    ) : (
                      <div className="admin-thumb-empty"><Home size={18} /></div>
                    )}
                  </td>
                  <td>
                    <span className="admin-prop-title" data-testid={`prop-title-${p.id}`}>{p.title}</span>
                    {p.featured && <span className="admin-featured-badge"><Star size={11} /> Destacada</span>}
                  </td>
                  <td>
                    <span className={`prop-badge ${p.operation === "renta" ? "renta" : ""}`} data-testid={`prop-operation-${p.id}`}>
                      {p.operation === "renta" ? "Renta" : "Venta"}
                    </span>
                  </td>
                  <td data-testid={`prop-price-${p.id}`}>{formatPrice(p.price)}</td>
                  <td>
                    <span className={`status-badge status-${p.status}`} data-testid={`prop-status-${p.id}`}>
                      {p.status === "disponible" ? "Disponible" : p.status === "vendido" ? "Vendido" : "Rentado"}
                    </span>
                  </td>
                  <td className="admin-actions-cell">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => openEdit(p.id)}
                      data-testid={`edit-btn-${p.id}`}
                      title="Editar"
                    >
                      <Pencil size={15} />
                    </button>
                    {confirmDeleteId === p.id ? (
                      <span className="delete-confirm" data-testid={`delete-confirm-${p.id}`}>
                        ¿Eliminar?
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)} data-testid={`delete-yes-${p.id}`}>Sí</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDeleteId(null)} data-testid={`delete-no-${p.id}`}>No</button>
                      </span>
                    ) : (
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteId(p.id)} data-testid={`delete-btn-${p.id}`} title="Eliminar">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
